import React, { FC, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import _ from "lodash";
import type { Datum } from "plotly.js";

import type { BoxStats, BoxWhiskerCategory, BoxWhiskerSeries } from "../../Widgets/Charts/BoxWhiskerChart";
import BoxWhiskerChart, { PLOT_HOVER_LINES } from "../../Widgets/Charts/BoxWhiskerChart";

import type { ProtocolBoxWhiskerChartProps } from "./types";

/** One scatter marker: a row's average, placed in its treatment's category. */
type ScatterPoint = {
	/** The treatment category (String(trialTreatmentId)). */
	x: string;
	y: number | null;
	plotName: string | null | undefined;
	rep: number | null | undefined;
	/** Compiled charts only. */
	trialName?: string | null;
	trialId?: string;
	city?: string | null;
	region?: string | null;
	countryCode?: string | null;
	cooperatorName?: string;
};

const ProtocolBoxWhiskerChart: FC<ProtocolBoxWhiskerChartProps> = ({
	data,
	tukeyData,
	tukeyRunning,
	assessmentName,
	trialName,
	axisYTitle,
	chartHeight,
	allTreatmentData,
	needToRerunStats,
	needToRerunNormalization,
	initialGroupTrialDataCheckedCount,
	isCompiled = undefined,
	allTreatmentsUnchecked = false,
	hideUncheckedTrialData = false,
	disableColors = false,
	hideMeanComparisonResults = false,
	chartType = "BoxWhisker",
}) => {
	const waitingForLetters = data.length > 0 && tukeyData?.length === 0;

	// TODO(types): entityNames is nullable in the redux state; this throws if it is null.
	const protocolName = useSelector((state) => state.entityNames!.protocolName ?? "");

	const getSnapshotTrialName = () => {
		if (trialName) return trialName;

		const trialNames = _.uniq(_.compact(_.map(data, "trialName")));
		return trialNames.length === 1 ? trialNames[0] : protocolName;
	};

	const getSnapshotAssessmentName = () => {
		const snapshotAssessmentName = assessmentName ?? axisYTitle;
		// String() matches exec's own coercion of null/undefined.
		return /\(([^)]+)\)/.exec(String(snapshotAssessmentName))?.[1] ?? snapshotAssessmentName;
	};

	const snapshotFileName = [getSnapshotTrialName(), getSnapshotAssessmentName(), chartType].filter(Boolean).join("_");

	/** The per-marker hover values, in the order getHoverLines reads them. */
	const getPointDetails = useCallback(
		(p: ScatterPoint): Datum[] => {
			// Cast: plotly accepts undefined entries (shown blank), but Datum excludes undefined.
			if (isCompiled === "averages") {
				return [p.trialName, p.city, p.region, p.countryCode, p.x] as Datum[];
			} else if (isCompiled === "allReps") {
				return [p.trialName, p.city, p.region, p.countryCode, p.plotName, p.x, p.rep] as Datum[];
			}
			return [p.plotName, p.x, p.rep] as Datum[];
		},
		[isCompiled],
	);

	/** The hover lines above the stats. Indices match getPointDetails. */
	const getHoverLines = useCallback(() => {
		if (isCompiled === "averages") {
			return [
				"<b>%{customdata[0]}</b>",
				"<b>%{customdata[1]}, %{customdata[2]}, %{customdata[3]}</b>",
				"<b>Treatment: </b>%{customdata[4]}",
				"<b>Avg. Value: </b>%{y}",
			];
		} else if (isCompiled === "allReps") {
			return [
				"<b>%{customdata[0]}</b>",
				"<b>%{customdata[1]}, %{customdata[2]}, %{customdata[3]}</b>",
				"<b>Plot: </b>%{customdata[4]}",
				"<b>Treatment: </b>%{customdata[5]}",
				"<b>Replicate: </b>%{customdata[6]}",
				"<b>Value: </b>%{y}",
			];
		}
		return PLOT_HOVER_LINES;
	}, [isCompiled]);

	const medianBy = useCallback((array: number[]) => {
		array.sort((a, b) => b - a);
		const length = array.length;
		if (length % 2 == 0) {
			return (array[length / 2] + array[length / 2 - 1]) / 2;
		} else {
			return array[Math.floor(length / 2)];
		}
	}, []);

	const getValuesAboveOrBelowMedian = useCallback(
		(values: number[], median: number, aboveMedian: boolean): number[] | 0 => {
			// TODO(types): returns 0, not an array, for empty input; callers only pass non-empty arrays.
			if (!values || values.length === 0) return 0;

			if (values.length === 1) return values;

			values.sort(function (a, b) {
				return a - b;
			});

			const half = Math.floor(values.length / 2);

			if (values.length % 2) {
				return aboveMedian ? values.slice(half + 1, values.length) : values.slice(0, half);
			}

			const allValuesAreTheSame = _.every(values, (v) => {
				return Number(v).toFixed(5) === Number(median).toFixed(5);
			});
			if (allValuesAreTheSame) {
				return [median];
			}

			return aboveMedian ? _.filter(values, (value) => value > median) : _.filter(values, (value) => value < median);
		},
		[],
	);

	const { categories, series } = useMemo(() => {
		const unGroupedScatterPlots: ScatterPoint[] = [];
		let isAnyTreatmentDeselected = false;
		const tempInitialGroupTrialDataCheckedCount = initialGroupTrialDataCheckedCount ?? [];

		const groupedTreatments = _.groupBy(data, "trialTreatmentId");
		const uniqueGroupedTreatments = _.uniqBy(data, "trialTreatmentId");
		const uniqueTreatmentsList = _.map(uniqueGroupedTreatments, (ugt) => ugt.trialTreatmentId);
		const uniqueGroupedTukeyTreatments = _.uniqBy(tukeyData, "trialTreatmentId");
		const tukeyDataList = _.map(uniqueGroupedTukeyTreatments, (ugt) => ugt.trialTreatmentId);
		const uniqueAllGroupedTreatments = allTreatmentData
			? _.uniqBy(allTreatmentData, "trialTreatmentId")
			: uniqueGroupedTreatments;
		const checkedTreatmentCountUpdated = _.map(uniqueAllGroupedTreatments, (d) => d.treatmentChecked);

		const treatmentIds: number[] = [];
		_.filter(tempInitialGroupTrialDataCheckedCount, (item, index) => {
			if (!item) {
				treatmentIds.push(index + 1);
			}
		});

		if (
			!_.isEqual(tempInitialGroupTrialDataCheckedCount, checkedTreatmentCountUpdated) &&
			tempInitialGroupTrialDataCheckedCount?.length !== 0
		) {
			isAnyTreatmentDeselected = true;
		} else if (initialGroupTrialDataCheckedCount?.length === 0) {
			if (
				_.some(uniqueGroupedTreatments, (d) => !d.treatmentChecked) ||
				_.some(checkedTreatmentCountUpdated, (ctcu) => ctcu === false)
			) {
				isAnyTreatmentDeselected = true;
			} else {
				isAnyTreatmentDeselected = false;
			}
		} else {
			isAnyTreatmentDeselected = false;
		}

		const boxX: string[] = [];
		const boxMin: number[] = [],
			boxQ1: number[] = [],
			boxMedian: number[] = [],
			boxQ3: number[] = [],
			boxMax: number[] = [];

		_.map(uniqueAllGroupedTreatments, (utr) => {
			const cat = String(utr.trialTreatmentId);
			const shouldRender =
				utr.treatmentChecked &&
				(_.isEmpty(tukeyDataList)
					? uniqueTreatmentsList.includes(utr.trialTreatmentId)
					: tukeyDataList.includes(utr.trialTreatmentId)) &&
				!needToRerunNormalization &&
				!allTreatmentsUnchecked &&
				!treatmentIds.includes(utr.trialTreatmentId) &&
				(_.some(utr.plots, (p) => p.plotId) || utr.plotName);

			boxX.push(cat);

			if (shouldRender) {
				// TODO(types): average is null on site rows for treatments with no data; Math.min/max and the
				// median sorts then treat null as 0.
				const areas = _.map(groupedTreatments[utr.trialTreatmentId], "average") as number[];

				_.map(groupedTreatments[utr.trialTreatmentId], (treatment) => {
					if (isCompiled) {
						unGroupedScatterPlots.push({
							x: cat,
							y: treatment.average,
							plotName: treatment.plotName ?? _.map(treatment.plots, "plotName").join(", "),
							trialName: treatment.trialName,
							trialId: treatment.trialId,
							city: treatment.city,
							region: treatment.region,
							countryCode: treatment.countryCode,
							cooperatorName: treatment.cooperatorName ?? "Unknown Cooperator",
							rep: treatment.plotReplicate ?? 0,
						});
					} else {
						unGroupedScatterPlots.push({
							x: cat,
							y: treatment.average,
							plotName: treatment.plotName,
							rep: treatment.plotReplicate,
						});
					}
				});

				if (areas.length > 0) {
					const median = Number(medianBy([...areas]).toFixed(5));
					// Never 0 here: areas is non-empty.
					const lowerValues = getValuesAboveOrBelowMedian([...areas], median, false) as number[];
					const higherValues = getValuesAboveOrBelowMedian([...areas], median, true) as number[];
					boxMin.push(Number(Math.min(...areas).toFixed(5)));
					boxQ1.push(Number(medianBy(lowerValues).toFixed(5)));
					boxMedian.push(median);
					boxQ3.push(Number(medianBy(higherValues).toFixed(5)));
					boxMax.push(Number(Math.max(...areas).toFixed(5)));
				} else {
					boxMin.push(NaN);
					boxQ1.push(NaN);
					boxMedian.push(NaN);
					boxQ3.push(NaN);
					boxMax.push(NaN);
				}
			} else {
				boxMin.push(NaN);
				boxQ1.push(NaN);
				boxMedian.push(NaN);
				boxQ3.push(NaN);
				boxMax.push(NaN);
			}
		});

		const scatterPlots = isCompiled
			? _.groupBy(unGroupedScatterPlots, "trialId")
			: _.groupBy(unGroupedScatterPlots, "rep");

		// Tukey letters. Left undefined when there are none, so the chart draws no letter row.
		const showLetters =
			!needToRerunStats &&
			!needToRerunNormalization &&
			!hideUncheckedTrialData &&
			!allTreatmentsUnchecked &&
			!hideMeanComparisonResults;
		const hasLetters = (tukeyData?.length ?? 0) > 0 && !tukeyRunning;

		// Unchecked treatments get no slot, so the axis only shows treatments that can have a box.
		// flatMap rather than filter-then-map: idx must stay aligned with the box* arrays.
		const categories: BoxWhiskerCategory[] = _.flatMap(uniqueAllGroupedTreatments, (utr, idx) => {
			if (!utr.treatmentChecked) return [];

			const stats: BoxStats = {
				min: boxMin[idx],
				q1: boxQ1[idx],
				median: boxMedian[idx],
				q3: boxQ3[idx],
				max: boxMax[idx],
			};
			if (!hasLetters) return [{ id: boxX[idx], stats }];

			const treatmentId = utr.trialTreatmentId;
			const tukeyEntry = _.find(tukeyData, (td) => td.trialTreatmentId === treatmentId);
			const hideThisLetter = !uniqueTreatmentsList.includes(treatmentId) || isAnyTreatmentDeselected;
			return [
				{
					id: boxX[idx],
					stats,
					letter: showLetters && tukeyEntry && !hideThisLetter ? (tukeyEntry.tukeyLetters ?? "") : "",
				},
			];
		});

		const series: BoxWhiskerSeries[] = Object.entries(scatterPlots).map(([key, sp], idx) => {
			// Replicates keep their palette color from chart to chart. Anything else takes the next one.
			const replicate = Number(key);
			return {
				name: isCompiled ? (sp[0].cooperatorName ?? key) : key,
				colorIndex: !isCompiled && Number.isInteger(replicate) && replicate >= 1 ? replicate - 1 : idx,
				points: sp.map((p) => ({
					categoryId: p.x,
					y: p.y,
					details: getPointDetails(p),
				})),
				hoverLines: getHoverLines(),
			};
		});

		return { categories, series };
	}, [
		allTreatmentData,
		allTreatmentsUnchecked,
		data,
		getHoverLines,
		getPointDetails,
		hideMeanComparisonResults,
		hideUncheckedTrialData,
		initialGroupTrialDataCheckedCount,
		isCompiled,
		needToRerunNormalization,
		needToRerunStats,
		tukeyData,
		medianBy,
		getValuesAboveOrBelowMedian,
		tukeyRunning,
	]);

	// Box is never thinner than a marker (8px) at minimum expected chart width (600px)
	// const boxWidth = Math.min(0.6, Math.max(0.2, (8 / 600) * boxProps.categories.length));

	return (
		<BoxWhiskerChart
			categories={categories}
			series={series}
			yAxisTitle={axisYTitle}
			legendTitle={isCompiled ? "Cooperators" : "Replicates"}
			loading={waitingForLetters}
			disableColors={disableColors}
			chartHeight={chartHeight}
			snapshotFileName={snapshotFileName}
		/>
	);
};

export default ProtocolBoxWhiskerChart;
